import { callable } from './callable.js'
import { uploadWorkspaceFiles } from './storageHubService.js'

// ---------------------------------------------------------------------------
// 1. Report Card Settings
// ---------------------------------------------------------------------------

export interface GetEduReportCardSettingsInput {
  workspaceId: string
}

export interface GetEduReportCardSettingsResult {
  reportCards: {
    showClassRank: boolean
    signer: {
      name: string
      title: string
      signaturePath: string | null
      signatureUrl?: string | null
    }
  }
}

export interface SetEduReportCardSettingsInput {
  workspaceId: string
  showClassRank?: boolean
  signer: {
    name: string
    title?: string
    signaturePath?: string | null
  }
}

export interface SetEduReportCardSettingsResult {
  success: boolean
  workspaceId: string
  reportCards: {
    showClassRank: boolean
    signer: {
      name: string
      title: string
      signaturePath: string | null
    }
  }
}

export async function getEduReportCardSettings(
  input: GetEduReportCardSettingsInput,
): Promise<GetEduReportCardSettingsResult> {
  return callable<GetEduReportCardSettingsInput, GetEduReportCardSettingsResult>(
    'getEduReportCardSettings',
    input,
  )
}

export async function setEduReportCardSettings(
  input: SetEduReportCardSettingsInput,
): Promise<SetEduReportCardSettingsResult> {
  return callable<SetEduReportCardSettingsInput, SetEduReportCardSettingsResult>(
    'setEduReportCardSettings',
    input,
  )
}

// ---------------------------------------------------------------------------
// 2. Report Remarks
// ---------------------------------------------------------------------------

export interface SetEduReportRemarksEntry {
  learnerId: string
  subjectNodeId: string | null
  text: string
}

export interface SetEduReportRemarksInput {
  workspaceId: string
  classId: string
  termId: string
  entries: SetEduReportRemarksEntry[]
}

export interface SetEduReportRemarksResult {
  success: boolean
  count: number
}

export async function setEduReportRemarks(
  input: SetEduReportRemarksInput,
): Promise<SetEduReportRemarksResult> {
  return callable<SetEduReportRemarksInput, SetEduReportRemarksResult>(
    'setEduReportRemarks',
    input,
  )
}

// ---------------------------------------------------------------------------
// 3. Co-Scholastic Grades
// ---------------------------------------------------------------------------

export interface SetEduCoScholasticGradesEntry {
  learnerId: string
  areaId: string
  label: string | null
}

export interface SetEduCoScholasticGradesInput {
  workspaceId: string
  classId: string
  termId: string
  entries: SetEduCoScholasticGradesEntry[]
}

export interface SetEduCoScholasticGradesResult {
  success: boolean
  count: number
}

export async function setEduCoScholasticGrades(
  input: SetEduCoScholasticGradesInput,
): Promise<SetEduCoScholasticGradesResult> {
  return callable<SetEduCoScholasticGradesInput, SetEduCoScholasticGradesResult>(
    'setEduCoScholasticGrades',
    input,
  )
}

// ---------------------------------------------------------------------------
// 4. Report Entries
// ---------------------------------------------------------------------------

export interface ListEduReportEntriesInput {
  workspaceId: string
  classId: string
  termId: string
}

export interface EduReportEntryDoc {
  id?: string
  workspaceId: string
  rootId: string
  classId: string
  termId: string
  learnerId: string
  overallRemark?: { text: string; by?: string; at?: unknown } | null
  subjectRemarks?: Record<string, { text: string; by?: string; at?: unknown }>
  coScholastic?: Record<string, { label: string; by?: string; at?: unknown }>
}

export interface EduReportCardRollEntry {
  learnerId: string
  displayName?: string
  rollNo?: string | null
  subjectIds: string[]
}

export interface ListEduReportEntriesResult {
  entries: EduReportEntryDoc[]
  roll?: EduReportCardRollEntry[]
}

export async function listEduReportEntries(
  input: ListEduReportEntriesInput,
): Promise<ListEduReportEntriesResult> {
  return callable<ListEduReportEntriesInput, ListEduReportEntriesResult>(
    'listEduReportEntries',
    input,
  )
}

// ---------------------------------------------------------------------------
// 5. Generate Drafts
// ---------------------------------------------------------------------------

export interface GenerateEduReportCardsInput {
  workspaceId: string
  classId: string
  termId: string
}

export interface GenerateEduReportCardsResult {
  created: number
  refreshed: number
  skippedPublished: number
  learners: Array<{
    learnerId: string
    cardId: string
    status: 'DRAFT' | 'PUBLISHED'
    kind: 'REPORT_CARD' | 'PROGRESS_REPORT'
  }>
}

export async function generateEduReportCards(
  input: GenerateEduReportCardsInput,
): Promise<GenerateEduReportCardsResult> {
  return callable<GenerateEduReportCardsInput, GenerateEduReportCardsResult>(
    'generateEduReportCards',
    input,
  )
}

// ---------------------------------------------------------------------------
// 6. List and Get Cards
// ---------------------------------------------------------------------------

export interface ListEduReportCardsInput {
  workspaceId: string
  classId: string
  termId: string
}

export interface EduReportCardPublishedVersion {
  version: number
  publishedAtMs: number
  updated: boolean
}

export interface EduReportCardSummary {
  id: string
  cardId: string
  status: 'DRAFT' | 'PUBLISHED'
  kind: 'REPORT_CARD' | 'PROGRESS_REPORT'
  version: number
  updated?: boolean
  versions?: EduReportCardPublishedVersion[]
  learner?: {
    learnerId: string
    displayName?: string
    rollNo?: string | null
  }
  termId?: string
  termName?: string
  snapshot?: {
    term?: {
      id?: string
      name?: string
    }
  }
  latestUnpublish?: {
    reason: string
    byUid: string
    atMs: number
  }
}

export interface ListEduReportCardsResult {
  cards: EduReportCardSummary[]
}

export async function listEduReportCards(
  input: ListEduReportCardsInput,
): Promise<ListEduReportCardsResult> {
  return callable<ListEduReportCardsInput, ListEduReportCardsResult>(
    'listEduReportCards',
    input,
  )
}

export interface GetEduReportCardInput {
  workspaceId: string
  cardId: string
}

export interface EduReportCardSubjectSnapshot {
  subjectNodeId: string
  name: string
  categoryAverages: Record<string, number | null>
  termPercent: number | null
  termGrade: string | null
  yearPercent: number | null
  yearGrade: string | null
  missingCount: number
  subjectRemark: string | null
}

export interface EduReportCardCoScholasticSnapshot {
  areaId: string
  name: string
  label: string | null
}

export interface EduReportCardSnapshot {
  school: {
    name: string
    logoUrl?: string | null
  }
  learner: {
    id: string
    displayName: string
    rollNo?: string | null
    className?: string
    rootName?: string
  }
  term: {
    id: string
    name: string
    startDate?: string
    endDate?: string
  }
  subjects: EduReportCardSubjectSnapshot[]
  attendance?: {
    present: number
    late: number
    absent: number
    onLeave: number
    excused: number
    percent: number | null
  } | null
  rank?: {
    rank: number | null
    classSize: number
    overallPercent: number | null
  } | null
  coScholastic?: EduReportCardCoScholasticSnapshot[]
  overallRemark?: string | null
  signer?: {
    name: string
    title: string
    signaturePath?: string | null
    signatureUrl?: string | null
    publishDate?: string | null
  } | null
  progress?: EduReportCardProgressSnapshot | null
}

export interface EduReportCardProgressMetricSnapshot {
  metricId: string
  name: string
  unit: string
  direction: 'HIGHER_BETTER' | 'LOWER_BETTER'
  target: number | null
  first: { value: number; date: string } | null
  latest: { value: number; date: string } | null
  change: { diff: number; trend: 'BETTER' | 'WORSE' | 'SAME' } | null
}

export interface EduReportCardProgressLevelSnapshot {
  current: {
    levelId: string
    name: string
    colour: string | null
    since: string | null
  } | null
  promotions: Array<{
    fromLevelId: string | null
    toLevelId: string
    fromLevelName?: string | null
    toLevelName: string
    date: string
    note?: string | null
  }>
}

export interface EduReportCardProgressPracticeSnapshot {
  totalMinutes: number
  logCount: number
}

export interface EduReportCardProgressSnapshot {
  metrics: EduReportCardProgressMetricSnapshot[]
  level: EduReportCardProgressLevelSnapshot
  practice: EduReportCardProgressPracticeSnapshot
}

export interface EduReportCardDoc {
  id?: string
  cardId?: string
  workspaceId: string
  rootId: string
  classId: string
  termId: string
  learnerId: string
  status: 'DRAFT' | 'PUBLISHED'
  kind: 'REPORT_CARD' | 'PROGRESS_REPORT'
  version: number
  updated?: boolean
  snapshot: EduReportCardSnapshot
  pdfPath?: string | null
  pdfStatus?: 'PENDING' | 'READY' | 'FAILED'
  pdfError?: string | null
  signedPdfUrl?: string | null
}

export type EduReportCard = EduReportCardDoc
export type EduReportCardEntry = EduReportEntryDoc

export interface GetEduReportCardResult {
  card: EduReportCardDoc
}

export async function getEduReportCard(
  input: GetEduReportCardInput,
): Promise<GetEduReportCardResult> {
  return callable<GetEduReportCardInput, GetEduReportCardResult>(
    'getEduReportCard',
    input,
  )
}

// ---------------------------------------------------------------------------
// 7. Publish and Unpublish
// ---------------------------------------------------------------------------

export interface PublishEduReportCardsInput {
  workspaceId: string
  classId: string
  termId: string
  learnerIds?: string[]
  confirmSigner: {
    name: string
    title?: string
  }
}

export interface PublishEduReportCardsResult {
  published: number
  skippedSignerMismatch?: number
  results?: Array<{
    cardId: string
    learnerId: string
    status: string
    version: number
  }>
}

export async function publishEduReportCards(
  input: PublishEduReportCardsInput,
): Promise<PublishEduReportCardsResult> {
  return callable<PublishEduReportCardsInput, PublishEduReportCardsResult>(
    'publishEduReportCards',
    input,
  )
}

export interface UnpublishEduReportCardsInput {
  workspaceId: string
  cardId?: string
  cardIds?: string[]
  reason: string
}

export interface UnpublishEduReportCardsResult {
  unpublished: number | boolean
}

export async function unpublishEduReportCards(
  input: UnpublishEduReportCardsInput,
): Promise<UnpublishEduReportCardsResult> {
  return callable<UnpublishEduReportCardsInput, UnpublishEduReportCardsResult>(
    'unpublishEduReportCards',
    input,
  )
}

// ---------------------------------------------------------------------------
// 8. PDF URL
// ---------------------------------------------------------------------------

export interface GetEduReportCardPdfUrlInput {
  workspaceId: string
  cardId: string
  version?: number
}

export interface GetEduReportCardPdfUrlResult {
  signedUrl: string
}

export async function getEduReportCardPdfUrl(
  input: GetEduReportCardPdfUrlInput,
): Promise<GetEduReportCardPdfUrlResult> {
  return callable<GetEduReportCardPdfUrlInput, GetEduReportCardPdfUrlResult>(
    'getEduReportCardPdfUrl',
    input,
  )
}

// ---------------------------------------------------------------------------
// 9. Report Center
// ---------------------------------------------------------------------------

export interface ListReportCenterEduCardsInput {
  workspaceId: string
}

export interface ListReportCenterEduCardsResult {
  cards: Array<{
    cardId: string
    learner?: { learnerId: string; displayName?: string; rollNo?: string | null }
    classId: string
    termId: string
    kind: string
    status: string
    version: number
    pdfStatus?: string
  }>
}

export async function listReportCenterEduCards(
  input: ListReportCenterEduCardsInput,
): Promise<ListReportCenterEduCardsResult> {
  return callable<ListReportCenterEduCardsInput, ListReportCenterEduCardsResult>(
    'listReportCenterEduCards',
    input,
  )
}

// ---------------------------------------------------------------------------
// 10. Family reads
// ---------------------------------------------------------------------------

export interface ListFamilyReportCardsInput {
  workspaceId: string
  learnerId: string
}

export interface ListFamilyReportCardsResult {
  cards: EduReportCardSummary[]
}

export async function listFamilyReportCards(
  input: ListFamilyReportCardsInput,
): Promise<ListFamilyReportCardsResult> {
  return callable<ListFamilyReportCardsInput, ListFamilyReportCardsResult>(
    'listFamilyReportCards',
    input,
  )
}

export interface GetFamilyReportCardInput {
  workspaceId: string
  cardId: string
  version?: number
}

export interface GetFamilyReportCardResult {
  card: EduReportCardDoc
}

export async function getFamilyReportCard(
  input: GetFamilyReportCardInput,
): Promise<GetFamilyReportCardResult> {
  return callable<GetFamilyReportCardInput, GetFamilyReportCardResult>(
    'getFamilyReportCard',
    input,
  )
}

// ---------------------------------------------------------------------------
// 11. Shared Card
// ---------------------------------------------------------------------------

export interface ReadSharedEduReportCardInput {
  shareId: string
  token: string
}

export interface ReadSharedEduReportCardResult {
  snapshot: EduReportCardSnapshot
  version: number
  signedPdfUrl: string | null
}

export async function readSharedEduReportCard(
  input: ReadSharedEduReportCardInput,
): Promise<ReadSharedEduReportCardResult> {
  return callable<ReadSharedEduReportCardInput, ReadSharedEduReportCardResult>(
    'readSharedEduReportCard',
    input,
  )
}

// ---------------------------------------------------------------------------
// 12. Signature Upload
// ---------------------------------------------------------------------------

export interface UploadReportCardSignatureResult {
  storagePath: string
  downloadUrl?: string
}

export async function uploadReportCardSignature(
  workspaceId: string,
  file: File,
): Promise<UploadReportCardSignatureResult> {
  const isImage =
    file.type === 'image/png' ||
    file.type === 'image/jpeg' ||
    file.type === 'image/jpg'
  if (!isImage) {
    throw new Error('Signature must be a PNG or JPEG image.')
  }
  if (file.size > 1024 * 1024) {
    throw new Error('Signature file size must not exceed 1 MB.')
  }
  const results = await uploadWorkspaceFiles({
    workspaceId,
    files: [file],
    visibility: 'WORKSPACE_SHARED',
    artifactType: 'EDU_SIGNATURE',
  })
  const res = results[0]
  const objectPath = res?.objectPath
  if (!objectPath) {
    throw new Error('Upload completed without returning a storage path.')
  }
  return {
    storagePath: objectPath,
    downloadUrl: res.downloadUrl,
  }
}
